import axios from 'axios';
import fnc from '../mymod/commFunction';

const serverUrl = (process.env.REACT_APP_SERVER_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:8002' : '')).replace(/\/$/, '');
const enabled = () => process.env.REACT_APP_AI_BILLING_ENABLED === 'true';
const pendingKey = 'kuesb.billing.pending';
const featureNames = { ai_study: 'AI 질문', essay_assess: '서술형 첨삭', omr_extract: '답안지 판독', omr_grade: '답안지 채점', scheduler_plan: '학습 계획' };
let activeBillingRequests = 0;
export const getActiveBillingRequests = () => activeBillingRequests;
export function notifyBillingUpdated() { window.dispatchEvent(new Event('billing:updated')); }
function changeActivity(change) {
  activeBillingRequests = Math.max(0, activeBillingRequests + change);
  window.dispatchEvent(new Event('billing:activity'));
}

export function billingAuth() {
  const token = fnc.getCookie('appSessionToken');
  if (!token) throw new Error('로그인이 필요합니다. 다시 로그인해 주세요.');
  return { Authorization: `Bearer ${token}` };
}

export async function billingPost(operation, data = {}) {
  try {
    const response = await axios.post(`${serverUrl}/billing/${operation}`, data, { headers: billingAuth(), timeout: 25000 });
    if (response.data?.success === false || response.data?.ok === false) throw new Error(response.data.error?.message || response.data.error || '크레딧 요청에 실패했습니다.');
    return response.data;
  } catch (error) {
    const message = error.response?.data?.error?.message || error.response?.data?.error;
    if (message) error.message = message;
    error.code = error.response?.data?.error?.code || error.response?.data?.errorCode || error.code;
    throw error;
  }
}

function requestId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
}

function rememberPending(job) {
  try {
    const items = JSON.parse(sessionStorage.getItem(pendingKey) || '[]');
    sessionStorage.setItem(pendingKey, JSON.stringify([...items.filter(item => item.requestId !== job.requestId), job].slice(-50)));
  } catch { /* Server job history remains authoritative when browser storage is unavailable. */ }
}

function pendingBillingJobs() {
  try {
    return JSON.parse(sessionStorage.getItem(pendingKey) || '[]');
  } catch { return []; }
}

export function finishBillingRequest(headers = {}) {
  const id = headers['Idempotency-Key'];
  if (!id) return;
  try {
    const items = JSON.parse(sessionStorage.getItem(pendingKey) || '[]');
    sessionStorage.setItem(pendingKey, JSON.stringify(items.filter(item => item.requestId !== id)));
  } catch { /* Wallet history can still be used. */ }
  notifyBillingUpdated();
}

export async function prepareAiBilling(featureCode, options = {}, fingerprint = '', existingRequestId = '') {
  if (!enabled()) return {};
  const auth = billingAuth();
  const quote = await billingPost('quote', { featureCode, ...options });
  if (billingAuth().Authorization !== auth.Authorization) throw new Error('로그인 정보가 변경되었습니다. 다시 실행해 주세요.');
  if (quote.enabled === false) throw new Error('AI 이용 서비스를 준비 중입니다. 잠시 후 다시 확인해 주세요.');
  const amount = quote.reservedCredits ?? quote.credits ?? quote.quote?.reservedCredits ?? quote.maxCredits;
  const available = quote.wallet?.availableCredits;
  if (!quote.quoteId || !Number.isSafeInteger(amount) || amount < 0 || !Number.isSafeInteger(available) || available < 0) {
    throw new Error('AI 요금을 확인하지 못했습니다. 다시 시도해 주세요.');
  }
  if (available < amount) {
    const error = new Error(`AI 크레딧이 부족합니다. 이번 이용에 ${amount.toLocaleString('ko-KR')} C가 필요하고, 사용 가능한 잔액은 ${available.toLocaleString('ko-KR')} C입니다. ‘지갑 · 충전’에서 충전한 뒤 다시 이용해 주세요.`);
    error.code = 'INSUFFICIENT_CREDITS';
    throw error;
  }
  const name = quote.name || featureNames[featureCode] || 'AI 이용';
  const model = quote.modelName || quote.modelCode;
  if (!model) throw new Error('AI 모델 정보를 확인하지 못했습니다. 다시 시도해 주세요.');
  const cost = `${quote.mode === 'token' ? '최대 ' : ''}${amount.toLocaleString('ko-KR')} C`;
  if (!window.confirm(`상품: ${name}\n모델: ${model}\n이번 이용 요금: ${cost}\n사용 가능한 잔액: ${available.toLocaleString('ko-KR')} C\n\n본인 지갑에서 이용 요금을 차감합니다. 계속하시겠습니까?`)) {
    const error = new Error('AI 이용을 취소했습니다.');
    error.code = 'ERR_CANCELED';
    throw error;
  }
  if (billingAuth().Authorization !== auth.Authorization) throw new Error('로그인 정보가 변경되었습니다. 다시 실행해 주세요.');
  const id = existingRequestId || requestId();
  rememberPending({ requestId: id, featureCode, fingerprint, createdAt: new Date().toISOString() });
  return { ...auth, 'Idempotency-Key': id, 'X-Billing-Quote-Id': quote.quoteId };
}

export const isBillingUiEnabled = enabled;

async function digest(value) {
  const bytes = typeof value === 'string' ? new TextEncoder().encode(value) : value;
  const result = await window.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(result), byte => byte.toString(16).padStart(2, '0')).join('');
}

async function fingerprintOf(featureCode, data) {
  let input = data;
  if (data instanceof FormData) {
    input = [];
    for (const [key, value] of data.entries()) {
      input.push([key, value instanceof Blob ? { type: value.type, hash: await digest(await value.arrayBuffer()) } : value]);
    }
  }
  // Keep only hashes in browser storage: no answer content or login credentials.
  return digest(JSON.stringify([fnc.getCookie('appSessionToken'), featureCode, input]));
}

const inFlight = new Map();
const recentlyCompleted = new Map();

function resultExpiredError() {
  const error = new Error('이미 완료된 AI 작업이며 답변 보관 기간(7일)이 지났습니다. 추가 차감은 없습니다. 새 작업은 다시 실행하고 이용 요금을 확인해 주세요.');
  error.code = 'BILLING_RESULT_EXPIRED';
  return error;
}

function isExpiredResponse(error) {
  const code = error.response?.data?.error?.code || error.response?.data?.errorCode || error.code;
  return error.response?.status === 410 && code === 'BILLING_RESULT_EXPIRED';
}

export async function postWithAiBilling(featureCode, url, data, config = {}, options = {}) {
  if (!enabled()) return axios.post(url, data, config);
  const originalToken = fnc.getCookie('appSessionToken');
  const fingerprint = await fingerprintOf(featureCode, options.fingerprintData || data);
  if (fnc.getCookie('appSessionToken') !== originalToken) throw new Error('로그인 정보가 변경되었습니다. 다시 실행해 주세요.');
  const recent = recentlyCompleted.get(fingerprint);
  if (recent && Date.now() - recent.at < 1000) return recent.response;
  if (inFlight.has(fingerprint)) return inFlight.get(fingerprint);
  const execute = async () => {
    const requireOriginalMember = () => {
      if (fnc.getCookie('appSessionToken') !== originalToken) throw new Error('로그인 정보가 변경되었습니다. 다시 실행해 주세요.');
    };
    const pending = pendingBillingJobs().find(item => item.fingerprint === fingerprint);
    let retryRequestId = '';
    if (pending) {
      let job;
      try { job = await billingPost('job', { requestId: pending.requestId }); }
      catch (error) {
        requireOriginalMember();
        if (isExpiredResponse(error)) {
          finishBillingRequest({ 'Idempotency-Key': pending.requestId });
          throw resultExpiredError();
        }
        if (error.response?.status !== 404) throw error;
      }
      requireOriginalMember();
      if (job?.status === 'completed' && job.resultExpired === true && job.resultAvailable === false
        && typeof job.resultExpiresAt === 'string' && Number.isFinite(Date.parse(job.resultExpiresAt)) && job.result === undefined) {
        finishBillingRequest({ 'Idempotency-Key': pending.requestId });
        throw resultExpiredError();
      }
      if (job?.status === 'completed' && job.result !== undefined && job.resultExpired !== true && job.resultAvailable !== false) {
        finishBillingRequest({ 'Idempotency-Key': pending.requestId });
        return { data: job.result };
      }
      if (job && !['released', 'failed'].includes(job.status)) {
        throw new Error('이미 접수된 AI 작업입니다. 내 AI 지갑에서 처리 상태와 차감 금액을 확인해 주세요.');
      }
      // The original request may still be uploading when lookup returns 404.
      if (!job) retryRequestId = pending.requestId;
      if (job) finishBillingRequest({ 'Idempotency-Key': pending.requestId });
    }
    if (fnc.getCookie('appSessionToken') !== originalToken) throw new Error('로그인 정보가 변경되었습니다. 다시 실행해 주세요.');
    const headers = await prepareAiBilling(featureCode, options.quote || {}, fingerprint, retryRequestId);
    if (fnc.getCookie('appSessionToken') !== originalToken) throw new Error('로그인 정보가 변경되었습니다. 다시 실행해 주세요.');
    try {
      const response = await axios.post(url, data, { ...config, headers: { ...config.headers, ...headers } });
      requireOriginalMember();
      finishBillingRequest(headers);
      return response;
    } catch (error) {
      requireOriginalMember();
      if (isExpiredResponse(error)) {
        finishBillingRequest(headers);
        throw resultExpiredError();
      }
      // A timeout can occur after charging. Keep the job until the server confirms its state.
      if ([401, 402, 403, 428].includes(error.response?.status)) finishBillingRequest(headers);
      if (error.response?.data?.error) error.message = error.response.data.error?.message || error.response.data.error;
      error.code = error.response?.data?.error?.code || error.response?.data?.errorCode || error.code;
      throw error;
    }
  };
  const promise = execute();
  changeActivity(1);
  inFlight.set(fingerprint, promise);
  try {
    const response = await promise;
    recentlyCompleted.set(fingerprint, { at: Date.now(), response });
    setTimeout(() => recentlyCompleted.delete(fingerprint), 1000);
    return response;
  }
  finally { inFlight.delete(fingerprint); changeActivity(-1); notifyBillingUpdated(); }
}
